import Axhelper from '@/lib/Axhelper'

class GminaService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			gminaAll: '/api/gmina/filters',
			gminaDetails: '/api/gmina/details',
			gminaSearch: '/api/gmina/filters',
			gminaCreate: '/api/gmina',
			gminaUpdate: '/api/gmina',
			gminaDelete: '/api/gmina',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			if (filters?.id_powiat) {
				params.append('id_powiat', String(filters.id_powiat))
			}
			if (filters?.id_wojewodztwo) {
				params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
			}
			const url = `${this.endpoints.gminaAll}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('GminaService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch gminy',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(gminaId) {
		try {
			if (!gminaId) {
				return {
					data: null,
					error: 'Invalid gmina ID provided',
				}
			}

			const url = `${this.endpoints.gminaDetails}/${encodeURIComponent(gminaId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('GminaService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch gmina details',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			if (!searchTerm) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			if (filters?.id_powiat) {
				params.append('id_powiat', String(filters.id_powiat))
			}
			if (filters?.id_wojewodztwo) {
				params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
			}
			const url = `${this.endpoints.gminaSearch}?searchText=${encodeURIComponent(searchTerm)}&${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('GminaService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search gminy',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(gminaData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.gminaCreate, gminaData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('GminaService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create gmina',
			}
		}
	}

	async update(gminaId, gminaData) {
		try {
			if (!gminaId) {
				return {
					success: false,
					error: 'Invalid gmina ID provided',
				}
			}

			const url = `${this.endpoints.gminaUpdate}/${encodeURIComponent(gminaId)}`
			const response = await Axhelper.sendPutAsync(url, gminaData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('GminaService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update gmina',
			}
		}
	}

	async delete(gminaId) {
		try {
			if (!gminaId) {
				return {
					success: false,
					error: 'Invalid gmina ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.gminaDelete}/${encodeURIComponent(gminaId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Gmina deleted successfully',
			}
		} catch (error) {
			console.error('GminaService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete gmina',
				message: null,
			}
		}
	}
}

export default new GminaService()
